import {useEffect, useState, useRef} from 'react';
import {useClickAway} from 'react-use';
import {UndoRedoManager} from '@utils/undoRedoManager';
import {toast} from 'react-toastify';
import {useHotkeys} from 'react-hotkeys-hook';

import {
    findProp,
    isRootNode,
    isSetupNode,
    Sgf,
    NodeAnnotationProp,
    Cursor,
    Ki,
    getMoveColor,
    getFirstToMoveColorFromRoot,
    isRightNode,
    isTargetNode,
    isForceNode,
    isPreventMoveNode,
    TNode,
} from 'ghostban';
import {
    MODAL_GLOBAL_BOARD_SETTINGS,
    showModal,
    useDispatch,
    useSgfTree,
    useSelector,
    findNextCharMarkupText,
    findNextNumberMarkupText,
} from '@utils';
import {Mode} from '@types';

import {BoardRefType, BoardToolbarButton} from '@components';
import {boardSlice} from '@reducers/board';
import {isMobile, isTablet} from 'react-device-detect';
import {useTranslation} from 'react-i18next';

const isTouchDevice = () => {
    const hasTouchCapability =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;

    const isMobileOrTablet = isMobile || isTablet;

    const result = hasTouchCapability || isMobileOrTablet;

    return result;
};

const MODE_CURSOR_MAP = {
    [Mode.Stone]: Cursor.BlackStone,
    [Mode.BlackStone]: Cursor.BlackStone,
    [Mode.WhiteStone]: Cursor.WhiteStone,
    [Mode.Circle]: Cursor.Circle,
    [Mode.Cross]: Cursor.Cross,
    [Mode.Triangle]: Cursor.Triangle,
    [Mode.Square]: Cursor.Square,
};

export const BoardToolbar = ({
    boardRef,
    showTagControls = false,
    showMarkupControls = true,
    showMetadataControls = false,
    showMoreButton = true,
    showMoveTool = true,
    showBranchControls = true,
    disabled = false,
    undoRedo,
    onHistoryRestore,
}: {
    boardRef: React.RefObject<BoardRefType | null>;
    undoRedo: UndoRedoManager;
    onHistoryRestore?: (
        item: import('@utils/undoRedoManager').HistoryItem
    ) => void;
    showTagControls?: boolean;
    showMarkupControls?: boolean;
    showMetadataControls?: boolean;
    showMoreButton?: boolean;
    showMoveTool?: boolean;
    showBranchControls?: boolean;
    disabled?: boolean;
}) => {
    const {t} = useTranslation();
    const {turn, cursor, sgf, mode, zoom, currentMarkupText} = useSelector(
        s => s.board
    );
    const {root, currentNode} = useSgfTree();
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const moreButtonRef = useRef<HTMLDivElement>(null);
    const floatingMenuRef = useRef<HTMLDivElement | null>(null);
    const [moreMenuPosition, setMoreMenuPosition] = useState({top: 0, left: 0});

    useEffect(() => {
        if (moreMenuOpen && moreButtonRef.current) {
            const rect = moreButtonRef.current.getBoundingClientRect();
            setTimeout(() => {
                if (floatingMenuRef.current) {
                    const menuRect =
                        floatingMenuRef.current.getBoundingClientRect();
                    setMoreMenuPosition({
                        top: rect.bottom + window.scrollY - menuRect.height,
                        left: isMobile
                            ? rect.left + window.scrollX - menuRect.width - 6
                            : rect.right + window.scrollX + 6,
                    });
                } else {
                    setMoreMenuPosition({
                        top: rect.bottom + window.scrollY - 26,
                        left: isMobile
                            ? rect.left + window.scrollX - 200 // approximate fallback width
                            : rect.right + window.scrollX + 6,
                    });
                }
            }, 0);
        }
    }, [moreMenuOpen]);

    useClickAway(floatingMenuRef, () => {
        if (moreMenuOpen) setMoreMenuOpen(false);
    });

    const FloatingMenu = () =>
        moreMenuOpen ? (
            <div
                ref={floatingMenuRef}
                className="fixed z-50"
                style={{
                    top: moreMenuPosition.top,
                    left: moreMenuPosition.left,
                }}>
                <div
                    className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white ${isMobile ? '-right-2' : '-left-2'}`}
                    style={{
                        clipPath: isMobile
                            ? 'polygon(100% 50%, 0 0, 0 100%)'
                            : 'polygon(0 50%, 100% 0, 100% 100%)',
                    }}
                />
                <div className="bg-white shadow-sm rounded-md p-2 flex flex-col gap-1 relative z-10">
                    {showBranchControls && (
                        <div className="flex gap-2">
                            <BoardToolbarButton
                                className="py-0 cursor-pointer"
                                iconRatio={0.35}
                                tooltip="Move branch up"
                                disabled={disabled}
                                tooltipPlacement="top"
                                icon={
                                    <svg
                                        className=""
                                        fill="currentColor"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 320 512">
                                        <path
                                            fill="currentColor"
                                            d="M318 177.5c3.8-8.8 2-19-4.6-26l-136-144C172.9 2.7 166.6 0 160 0s-12.9 2.7-17.4 7.5l-136 144c-6.6 7-8.4 17.2-4.6 26S14.4 192 24 192l72 0 0 288c0 17.7 14.3 32 32 32l64 0c17.7 0 32-14.3 32-32l0-288 72 0c9.6 0 18.2-5.7 22-14.5z"
                                        />
                                    </svg>
                                }
                                onClick={() => {
                                    if (!currentNode) return;
                                    let node = currentNode;
                                    let nodeIndex = node.getIndex();
                                    while (node.parent) {
                                        if (node.getIndex() > 0) {
                                            nodeIndex = node.getIndex();
                                            break;
                                        }
                                        node = node.parent;
                                    }
                                    if (node.isRoot() || nodeIndex <= 0) return;
                                    node.setIndex(nodeIndex - 1);
                                    dispatch(
                                        boardSlice.actions.setCurrentNodeId(
                                            node.model.id
                                        )
                                    );
                                    dispatch(boardSlice.actions.forceUpdate());
                                }}
                            />
                            <BoardToolbarButton
                                className="py-0 cursor-pointer"
                                disabled={disabled}
                                iconRatio={0.35}
                                tooltip="Move branch down"
                                tooltipPlacement="top"
                                icon={
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="currentColor"
                                        viewBox="0 0 320 512">
                                        <path
                                            fill="currentColor"
                                            d="M2 334.5c-3.8 8.8-2 19 4.6 26l136 144c4.5 4.8 10.8 7.5 17.4 7.5s12.9-2.7 17.4-7.5l136-144c6.6-7 8.4-17.2 4.6-26s-12.5-14.5-22-14.5l-72 0 0-288c0-17.7-14.3-32-32-32L128 0C110.3 0 96 14.3 96 32l0 288-72 0c-9.6 0-18.2 5.7-22 14.5z"
                                        />
                                    </svg>
                                }
                                onClick={() => {
                                    if (!currentNode) return;
                                    let node: TNode | undefined = currentNode;
                                    let nodeIndex = node.getIndex();
                                    while (node.parent) {
                                        if (
                                            node.getIndex() <
                                            node.parent.children.length - 1
                                        ) {
                                            nodeIndex = node.getIndex();
                                            break;
                                        }
                                        node = node.parent;
                                    }
                                    if (
                                        node.isRoot() ||
                                        (node.parent &&
                                            nodeIndex >=
                                                node.parent.children.length - 1)
                                    )
                                        return;
                                    node.setIndex(nodeIndex + 1);
                                    dispatch(
                                        boardSlice.actions.setCurrentNodeId(
                                            node.model.id
                                        )
                                    );
                                    dispatch(boardSlice.actions.forceUpdate());
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        ) : null;

    useHotkeys(
        ['Meta+z', 'Control+z'],
        () => {
            handleUndo();
        },
        {preventDefault: true}
    );

    useHotkeys(
        ['Meta+Shift+z', 'Control+Shift+z'],
        () => {
            handleRedo();
        },
        {preventDefault: true}
    );

    useHotkeys(
        ['m'],
        () => {
            handleMoveToolClick();
        },
        {preventDefault: true}
    );

    useHotkeys(
        ['s'],
        () => {
            if (disabled) return;
            dispatch(boardSlice.actions.setMode(Mode.Stone));
        },
        {preventDefault: true}
    );

    useHotkeys(
        ['r'],
        () => {
            bindStateButton('RIGHT');
        },
        {preventDefault: true}
    );

    const dispatch = useDispatch();

    const board = boardRef.current?.getBoard();

    // TODO: Duplicate code
    const saveHistory = (currentNode: TNode, updateSelection = false) => {
        const sgf = new Sgf(root);
        const parsedSgfText = sgf.toSgf();
        undoRedo.add(parsedSgfText, currentNode.model.id);
        if (updateSelection) {
            undoRedo.replace(parsedSgfText, currentNode.model.id);
        }
    };

    const handleNodeDelete = () => {
        if (!currentNode || disabled) return;
        const node = currentNode.parent;

        if (node) {
            saveHistory(currentNode, true);
            currentNode.drop();
            dispatch(boardSlice.actions.setCurrentNodeId(node.model.id));
            dispatch(boardSlice.actions.forceUpdate());
            saveHistory(node);
        } else {
            // TODO: replaced alert with notification popup
            alert(t('problem.root_node_cannot_be_deleted'));
        }
    };

    const bindStateButton = (extraText: string) => {
        if (!currentNode || disabled) return;
        const commentProp = findProp(currentNode, 'C');
        if (!commentProp) {
            currentNode.model.nodeAnnotationProps = [
                ...currentNode.model.nodeAnnotationProps,
                new NodeAnnotationProp('C', extraText),
            ];
        } else {
            if (commentProp.value.includes(extraText)) {
                const replacedValue = commentProp.value.replaceAll(
                    extraText,
                    ''
                );
                if (replacedValue !== '') {
                    commentProp.value = replacedValue;
                } else {
                    currentNode.model.nodeAnnotationProps =
                        currentNode.model.nodeAnnotationProps.filter(
                            (n: NodeAnnotationProp) => n.token !== 'C'
                        );
                }
            } else {
                commentProp.value += extraText;
            }
        }
        saveHistory(currentNode);
        dispatch(boardSlice.actions.forceUpdate());
    };

    const updateLetterMarkupText = (node?: TNode) => {
        if (!node) return;
        const nextChar = findNextCharMarkupText(node);
        dispatch(boardSlice.actions.setCursor(Cursor.Text));
        dispatch(boardSlice.actions.setCurrentMarkupText(nextChar));
    };

    const updateNumberMarkupText = (node?: TNode) => {
        if (!node) return;
        const nextNum = findNextNumberMarkupText(node);
        dispatch(boardSlice.actions.setCursor(Cursor.Text));
        dispatch(boardSlice.actions.setCurrentMarkupText(nextNum));
    };

    const handleUndo = () => {
        if (disabled) return;
        const result = undoRedo.undo();
        if (result) {
            dispatch(boardSlice.actions.setSgf(result.sgfContent));
            dispatch(
                boardSlice.actions.setCurrentNodeId(result.currentNodeIndex)
            );
            dispatch(boardSlice.actions.forceUpdate());
            onHistoryRestore?.(result);
        } else {
            toast.info('The current version is already the oldest.');
        }
    };

    const handleRedo = () => {
        if (disabled) return;
        const result = undoRedo.redo();
        if (result) {
            dispatch(boardSlice.actions.setSgf(result.sgfContent));
            dispatch(
                boardSlice.actions.setCurrentNodeId(result.currentNodeIndex)
            );
            dispatch(boardSlice.actions.forceUpdate());
            onHistoryRestore?.(result);
        } else {
            toast.info('The current version is already the latest.');
        }
    };

    const handleMoveToolClick = () => {
        if (!currentNode || disabled) return;
        dispatch(boardSlice.actions.setMode(Mode.Move));
        if (currentNode.isRoot() && !currentNode.hasChildren()) {
            const nextTurn = -turn;
            dispatch(boardSlice.actions.setTurn(nextTurn));
            dispatch(
                boardSlice.actions.setCursor(
                    nextTurn === Ki.White
                        ? Cursor.WhiteStone
                        : Cursor.BlackStone
                )
            );
        }
    };

    useEffect(() => {
        if (!currentNode) return;
        // const turn = getFirstToMoveColorFromRoot(root, Ki.Black);
        // const moveColor = getMoveColor(currentNode);
        switch (mode) {
            case Mode.Stone:
            case Mode.BlackStone:
            case Mode.WhiteStone:
            case Mode.Circle:
            case Mode.Cross:
            case Mode.Square:
            case Mode.Triangle: {
                dispatch(boardSlice.actions.setCursor(MODE_CURSOR_MAP[mode]));
                break;
            }
            case Mode.Move: {
                let nextMoveColor = Ki.Empty;
                if (isRootNode(currentNode) || isSetupNode(currentNode)) {
                    nextMoveColor = getFirstToMoveColorFromRoot(root);
                } else {
                    const moveColor = getMoveColor(currentNode);
                    if (moveColor === Ki.Black || moveColor === Ki.White) {
                        nextMoveColor = -moveColor;
                    }
                }

                if (nextMoveColor !== Ki.Empty) {
                    const cursor =
                        nextMoveColor === Ki.Black
                            ? Cursor.BlackStone
                            : Cursor.WhiteStone;
                    dispatch(boardSlice.actions.setCursor(cursor));
                    dispatch(boardSlice.actions.setTurn(nextMoveColor));
                }
                break;
            }
            default: {
                break;
            }
        }
    }, [currentNode, mode, dispatch, root]);

    useEffect(() => {
        if (!currentNode || !sgf || !board) return;

        board.render();
    }, [currentNode, turn, cursor, sgf, board]);

    const touchDevice = isTouchDevice();

    return (
        <div className="flex !my-1 overflow-auto md:!my-0 md:flex-col gap-0.5 md:pl-2 md:pr-2">
            {!touchDevice && (
                <BoardToolbarButton
                    iconPath="/build/images/icon/black_stone.webp"
                    tooltip="Setup (right-click for white stones, drag to place continuously)"
                    disabled={disabled}
                    active={Mode.Stone === mode}
                    onClick={() =>
                        dispatch(boardSlice.actions.setMode(Mode.Stone))
                    }
                />
            )}
            {touchDevice && (
                <>
                    <BoardToolbarButton
                        iconPath="/build/images/icon/black_stone.webp"
                        tooltip="Setup (Black)"
                        active={Mode.BlackStone === mode}
                        disabled={disabled}
                        onClick={() =>
                            dispatch(
                                boardSlice.actions.setMode(Mode.BlackStone)
                            )
                        }
                    />
                    <BoardToolbarButton
                        iconPath="/build/images/icon/white_stone.webp"
                        tooltip="Setup (White)"
                        active={Mode.WhiteStone === mode}
                        disabled={disabled}
                        onClick={() =>
                            dispatch(
                                boardSlice.actions.setMode(Mode.WhiteStone)
                            )
                        }
                    />
                </>
            )}
            {showMoveTool && (
                <BoardToolbarButton
                    iconPath={
                        turn === 1
                            ? '/build/images/icon/black_move.svg'
                            : '/build/images/icon/white_move.svg'
                    }
                    tooltip="Move Tool"
                    iconRatio={1}
                    active={Mode.Move === mode}
                    disabled={disabled}
                    onClick={() => {
                        handleMoveToolClick();
                    }}
                />
            )}
            <BoardToolbarButton
                icon={
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="16"
                        width="14"
                        fill={'currentColor'}
                        viewBox="0 0 448 512">
                        <path
                            stroke="currentColor"
                            fill="currentColor"
                            d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0H284.2c12.1 0 23.2 6.8 28.6 17.7L320 32h96c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 96 0 81.7 0 64S14.3 32 32 32h96l7.2-14.3zM32 128H416V448c0 35.3-28.7 64-64 64H96c-35.3 0-64-28.7-64-64V128zm96 64c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16z"
                        />
                    </svg>
                }
                disabled={disabled}
                tooltip="Delete Node"
                onClick={handleNodeDelete}
            />
            {showMarkupControls && (
                <>
                    <hr className="board-toolbar-divider" />
                    <BoardToolbarButton
                        icon={
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="100%"
                                width="100%"
                                fill={'currentColor'}
                                viewBox="0 0 512 512">
                                <path
                                    stroke="currentColor"
                                    fill="currentColor"
                                    d="M464 256A208 208 0 1 0 48 256a208 208 0 1 0 416 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256z"
                                />
                            </svg>
                        }
                        tooltip="Circle Tool"
                        active={Mode.Circle === mode}
                        disabled={disabled}
                        onClick={() =>
                            dispatch(boardSlice.actions.setMode(Mode.Circle))
                        }
                    />
                    <BoardToolbarButton
                        icon={
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="100%"
                                width="100%"
                                fill={'currentColor'}
                                viewBox="0 0 448 512">
                                <path
                                    stroke="currentColor"
                                    fill="currentColor"
                                    d="M384 80c8.8 0 16 7.2 16 16V416c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V96c0-8.8 7.2-16 16-16H384zM64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64z"
                                />
                            </svg>
                        }
                        tooltip="Square Tool"
                        active={Mode.Square === mode}
                        disabled={disabled}
                        onClick={() =>
                            dispatch(boardSlice.actions.setMode(Mode.Square))
                        }
                    />
                    <BoardToolbarButton
                        icon={
                            <svg
                                fill={
                                    disabled ? 'currentColor' : 'currentColor'
                                }
                                viewBox="0 0 100 100"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M50 10 L90 85 H10 Z"
                                    fill="none"
                                    stroke={'currentColor'}
                                    strokeWidth="10"
                                />
                            </svg>
                        }
                        tooltip="Triangle Tool"
                        disabled={disabled}
                        active={Mode.Triangle === mode}
                        onClick={() =>
                            dispatch(boardSlice.actions.setMode(Mode.Triangle))
                        }
                    />
                    <BoardToolbarButton
                        icon={
                            <svg
                                fill={'currentColor'}
                                viewBox="0 0 100 100"
                                xmlns="http://www.w3.org/2000/svg">
                                <line
                                    x1="20"
                                    y1="20"
                                    x2="80"
                                    y2="80"
                                    stroke="currentColor"
                                    strokeWidth="10"
                                    strokeLinecap="round"
                                />
                                <line
                                    x1="80"
                                    y1="20"
                                    x2="20"
                                    y2="80"
                                    stroke="currentColor"
                                    strokeWidth="10"
                                    strokeLinecap="round"
                                />
                            </svg>
                        }
                        tooltip="Cross Tool"
                        disabled={disabled}
                        active={Mode.Cross === mode}
                        onClick={() =>
                            dispatch(boardSlice.actions.setMode(Mode.Cross))
                        }
                    />
                    <BoardToolbarButton
                        icon={<label className="font-semibold">AB</label>}
                        iconRatio={0.8}
                        disabled={disabled}
                        tooltip="Label Tool"
                        active={Mode.Letter === mode}
                        onClick={() => {
                            dispatch(boardSlice.actions.setMode(Mode.Letter));
                            updateLetterMarkupText(currentNode);
                        }}
                    />
                    <BoardToolbarButton
                        icon={<label className="font-semibold">01</label>}
                        iconRatio={0.8}
                        disabled={disabled}
                        tooltip="Number Tool"
                        active={Mode.Number === mode}
                        onClick={() => {
                            dispatch(boardSlice.actions.setMode(Mode.Number));
                            updateNumberMarkupText(currentNode);
                        }}
                    />
                    {(mode === Mode.Letter || mode === Mode.Number) && (
                        <input
                            className="text-center my-1"
                            style={{width: 34}}
                            value={currentMarkupText}
                            onChange={e => {
                                dispatch(
                                    boardSlice.actions.setCurrentMarkupText(
                                        e.target.value
                                    )
                                );
                            }}
                        />
                    )}
                </>
            )}
            <hr className="board-toolbar-divider" />
            <BoardToolbarButton
                iconRatio={0.8}
                icon={
                    <svg
                        width="24px"
                        height="24px"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M4 7H15C16.8692 7 17.8039 7 18.5 7.40193C18.9561 7.66523 19.3348 8.04394 19.5981 8.49999C20 9.19615 20 10.1308 20 12C20 13.8692 20 14.8038 19.5981 15.5C19.3348 15.9561 18.9561 16.3348 18.5 16.5981C17.8039 17 16.8692 17 15 17H8.00001M4 7L7 4M4 7L7 10"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                }
                disabled={disabled}
                tooltip="Undo"
                onClick={handleUndo}
            />
            <BoardToolbarButton
                iconRatio={0.8}
                icon={
                    <svg
                        style={{transform: 'rotateY(180deg)'}}
                        width="24px"
                        height="24px"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M4 7H15C16.8692 7 17.8039 7 18.5 7.40193C18.9561 7.66523 19.3348 8.04394 19.5981 8.49999C20 9.19615 20 10.1308 20 12C20 13.8692 20 14.8038 19.5981 15.5C19.3348 15.9561 18.9561 16.3348 18.5 16.5981C17.8039 17 16.8692 17 15 17H8.00001M4 7L7 4M4 7L7 10"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                }
                disabled={disabled}
                tooltip="Redo"
                onClick={handleRedo}
            />
            <BoardToolbarButton
                iconRatio={0.8}
                icon={
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="16"
                        width="16"
                        fill={'currentColor'}
                        viewBox="0 0 512 512">
                        <path
                            stroke="currentColor"
                            fill="currentColor"
                            d="M495.9 166.6c3.2 8.7 .5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6c-4.4 11.9-9.7 23.3-15.8 34.3l-4.7 8.1c-6.6 11-14 21.4-22.1 31.2c-5.9 7.2-15.7 9.6-24.5 6.8l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8c-13.8 2.3-28 3.5-42.5 3.5s-28.7-1.2-42.5-3.5c-9.2-1.5-16.2-8.7-18.2-17.8l-12.5-57.1c-15.8-6.5-30.6-15.1-44-25.4L83.1 425.9c-8.8 2.8-18.6 .3-24.5-6.8c-8.1-9.8-15.5-20.2-22.1-31.2l-4.7-8.1c-6.1-11-11.4-22.4-15.8-34.3c-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L22.4 191.2c-6.9-6.2-9.6-15.9-6.4-24.6c4.4-11.9 9.7-23.3 15.8-34.3l4.7-8.1c6.6-11 14-21.4 22.1-31.2c5.9-7.2 15.7-9.6 24.5-6.8l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8C227.3 1.2 241.5 0 256 0s28.7 1.2 42.5 3.5c9.2 1.5 16.2 8.7 18.2 17.8l12.5 57.1c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.8 18.6-.3 24.5 6.8c8.1 9.8 15.5 20.2 22.1 31.2l4.7 8.1c6.1 11 11.4 22.4 15.8 34.3zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z"
                        />
                    </svg>
                }
                disabled={disabled}
                tooltip="Board Setting"
                onClick={() => {
                    showModal(MODAL_GLOBAL_BOARD_SETTINGS);
                }}
            />
            <BoardToolbarButton
                iconRatio={0.8}
                icon={
                    zoom ? (
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                fill="currentColor"
                                d="M3.9 21.5L2.5 20.1L5.6 17H3V15H9V21H7V18.4L3.9 21.5ZM20.1 21.5L17 18.4V21H15V15H21V17H18.4L21.5 20.1L20.1 21.5ZM3 9V7H5.6L2.5 3.9L3.9 2.5L7 5.6V3H9V9H3ZM15 9V3H17V5.6L20.1 2.5L21.5 3.9L18.4 7H21V9H15Z"
                            />
                        </svg>
                    ) : (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="24"
                            viewBox="0 -960 960 960"
                            width="24">
                            <path
                                fill="currentColor"
                                d="M120-120v-240h80v104l124-124 56 56-124 124h104v80H120Zm480 0v-80h104L580-324l56-56 124 124v-104h80v240H600ZM324-580 200-704v104h-80v-240h240v80H256l124 124-56 56Zm312 0-56-56 124-124H600v-80h240v240h-80v-104L636-580Z"
                            />
                        </svg>
                    )
                }
                tooltip="Zoom"
                onClick={() => dispatch(boardSlice.actions.toggleZoom())}
            />
            {showMetadataControls && (
                <>
                    <hr className="board-toolbar-divider" />
                    <BoardToolbarButton
                        icon={
                            <svg
                                viewBox="0 0 100 100"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="currentColor">
                                <polygon
                                    points="50,15 80,50 50,85 20,50"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="8"
                                />
                            </svg>
                        }
                        tooltip="Target Position"
                        active={Mode.TargetPosition === mode}
                        disabled={disabled}
                        onClick={() =>
                            dispatch(
                                boardSlice.actions.setMode(Mode.TargetPosition)
                            )
                        }
                    />
                    <BoardToolbarButton
                        icon={
                            <svg
                                viewBox="0 0 100 100"
                                xmlns="http://www.w3.org/2000/svg">
                                <rect
                                    x="15"
                                    y="15"
                                    width="70"
                                    height="70"
                                    fill="none"
                                    stroke="rgba(34, 197, 94, 0.9)"
                                    strokeWidth="8"
                                    rx="4"
                                />
                            </svg>
                        }
                        tooltip="Player Allowed Area"
                        active={Mode.PlayerAllowedArea === mode}
                        disabled={disabled}
                        onClick={() =>
                            dispatch(
                                boardSlice.actions.setMode(
                                    Mode.PlayerAllowedArea
                                )
                            )
                        }
                    />
                    <BoardToolbarButton
                        icon={
                            <svg
                                viewBox="0 0 100 100"
                                xmlns="http://www.w3.org/2000/svg">
                                <rect
                                    x="15"
                                    y="15"
                                    width="70"
                                    height="70"
                                    fill="none"
                                    stroke="rgba(239, 68, 68, 0.9)"
                                    strokeWidth="8"
                                    rx="4"
                                />
                            </svg>
                        }
                        tooltip="Player Disallowed Area"
                        active={Mode.PlayerDisallowedArea === mode}
                        disabled={disabled}
                        onClick={() =>
                            dispatch(
                                boardSlice.actions.setMode(
                                    Mode.PlayerDisallowedArea
                                )
                            )
                        }
                    />
                    <BoardToolbarButton
                        icon={
                            <svg
                                viewBox="0 0 100 100"
                                xmlns="http://www.w3.org/2000/svg">
                                <rect
                                    x="15"
                                    y="15"
                                    width="70"
                                    height="70"
                                    fill="none"
                                    stroke="rgba(34, 197, 94, 0.9)"
                                    strokeWidth="8"
                                    strokeDasharray="12 6"
                                    rx="4"
                                />
                            </svg>
                        }
                        tooltip="Computer Allowed Area"
                        active={Mode.ComputerAllowedArea === mode}
                        disabled={disabled}
                        onClick={() =>
                            dispatch(
                                boardSlice.actions.setMode(
                                    Mode.ComputerAllowedArea
                                )
                            )
                        }
                    />
                    <BoardToolbarButton
                        icon={
                            <svg
                                viewBox="0 0 100 100"
                                xmlns="http://www.w3.org/2000/svg">
                                <rect
                                    x="15"
                                    y="15"
                                    width="70"
                                    height="70"
                                    fill="none"
                                    stroke="rgba(239, 68, 68, 0.9)"
                                    strokeWidth="8"
                                    strokeDasharray="12 6"
                                    rx="4"
                                />
                            </svg>
                        }
                        tooltip="Computer Disallowed Area"
                        active={Mode.ComputerDisallowedArea === mode}
                        disabled={disabled}
                        onClick={() =>
                            dispatch(
                                boardSlice.actions.setMode(
                                    Mode.ComputerDisallowedArea
                                )
                            )
                        }
                    />
                </>
            )}
            {showTagControls && (
                <>
                    <hr className="board-toolbar-divider" />
                    <BoardToolbarButton
                        active={currentNode && isRightNode(currentNode)}
                        iconRatio={0.9}
                        icon={
                            <img
                                src="/build/images/icon/right.webp"
                                alt="right"
                            />
                        }
                        tooltip="Right"
                        disabled={
                            disabled || (currentNode && isRootNode(currentNode))
                        }
                        onClick={() => {
                            bindStateButton('RIGHT');
                        }}
                    />
                    <BoardToolbarButton
                        iconRatio={0.9}
                        icon={
                            <img
                                src="/build/images/icon/notthis.webp"
                                alt="notthis"
                            />
                        }
                        active={currentNode && isPreventMoveNode(currentNode)}
                        tooltip="Prevent Move"
                        disabled={
                            disabled || (currentNode && isRootNode(currentNode))
                        }
                        onClick={() => {
                            bindStateButton('NOTTHIS');
                        }}
                    />
                    <BoardToolbarButton
                        iconRatio={0.9}
                        icon={
                            <img
                                src="/build/images/icon/force.webp"
                                alt="force"
                            />
                        }
                        active={currentNode && isForceNode(currentNode)}
                        tooltip="Force"
                        disabled={disabled}
                        onClick={() => {
                            bindStateButton('FORCE');
                        }}
                    />
                    <BoardToolbarButton
                        iconRatio={0.9}
                        icon={
                            <img
                                src="/build/images/icon/target.webp"
                                alt="target"
                            />
                        }
                        active={currentNode && isTargetNode(currentNode)}
                        tooltip="Target"
                        disabled={
                            disabled || (currentNode && isRootNode(currentNode))
                        }
                        onClick={() => {
                            bindStateButton('CHOICE');
                        }}
                    />
                </>
            )}
            {showMoreButton && showBranchControls && (
                <>
                    <hr className="board-toolbar-divider" />
                    <div className="relative" ref={moreButtonRef}>
                        <BoardToolbarButton
                            className="py-0 cursor-pointer"
                            height={isMobile ? undefined : 26}
                            disabled={disabled}
                            iconRatio={0.6}
                            icon={
                                <svg
                                    fill="currentColor"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512">
                                    <path
                                        fill="currentColor"
                                        d="M8 256a56 56 0 1 1 112 0A56 56 0 1 1 8 256zm160 0a56 56 0 1 1 112 0 56 56 0 1 1 -112 0zm216-56a56 56 0 1 1 0 112 56 56 0 1 1 0-112z"
                                    />
                                </svg>
                            }
                            onClick={() => {
                                setMoreMenuOpen(!moreMenuOpen);
                            }}
                        />
                        <FloatingMenu />
                    </div>
                </>
            )}
        </div>
    );
};

export default BoardToolbar;
